import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getCcOverview, flagCcTransaction, tagCcTransaction, linkDocToTxn } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { CreditCard, ArrowLeftRight, UserX, DollarSign, RotateCcw, FileText, AlertTriangle, CheckCircle2, User, Briefcase, Paperclip } from 'lucide-react';

function TxnRow({ txn, actions }) {
  return (
    <div className="px-5 py-3 flex items-center gap-4 hover:bg-[rgba(198,156,109,0.02)] transition-colors" style={{ borderBottom: '1px solid #E5E3DB' }} data-testid={`cc-txn-${txn.transaction_id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="text-sm font-body font-medium truncate" style={{ color: '#1A1A1A' }}>{txn.vendor_name || 'Unknown'}</span>
          <span className="font-mono text-xs" style={{ color: '#9CA3AF' }}>{txn.date}</span>
        </div>
        <p className="text-xs font-body truncate" style={{ color: '#4B5563' }}>{txn.description}</p>
        {txn.risk_reason && <p className="text-[10px] font-body mt-0.5" style={{ color: '#C84B31' }}>{txn.risk_reason}</p>}
      </div>
      <span className="font-mono text-sm font-medium flex-shrink-0" style={{ color: txn.amount < 0 ? '#2D7A5D' : '#1A1A1A' }}>
        ${Math.abs(txn.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}
      </span>
      <div className="flex gap-1.5 flex-shrink-0">{actions}</div>
    </div>
  );
}

function StatCard({ label, value, sub, icon: Icon, color }) {
  return (
    <div className="bg-white rounded-xl p-5" style={{ border: '1px solid #E5E3DB' }}>
      <div className="flex items-center justify-between">
        <div>
          <p className="text-[10px] font-body uppercase tracking-[0.2em] font-semibold mb-2" style={{ color: '#9CA3AF' }}>{label}</p>
          <p className="text-2xl font-heading font-medium" style={{ color: '#1A1A1A' }}>{value}</p>
          {sub && <p className="text-xs font-body mt-1" style={{ color: '#4B5563' }}>{sub}</p>}
        </div>
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ background: `${color}15` }}>
          <Icon size={20} style={{ color }} strokeWidth={1.8} />
        </div>
      </div>
    </div>
  );
}

export default function CreditCardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const res = await getCcOverview();
      setData(res.data);
    } catch { toast.error('Failed to load credit card data'); }
    setLoading(false);
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleFlag = async (txnId, flagType) => {
    try {
      await flagCcTransaction(txnId, { flag_type: flagType });
      toast.success(`Flagged as ${flagType}`);
      fetchData();
    } catch { toast.error('Failed to flag'); }
  };

  const handleTag = async (txnId, tag) => {
    try {
      await tagCcTransaction(txnId, { tag });
      toast.success(`Tagged as ${tag}`);
      fetchData();
    } catch { toast.error('Failed to tag'); }
  };

  const handleLinkDoc = async (txnId) => {
    try {
      await linkDocToTxn(txnId, { doc_type: 'receipt' });
      toast.success('Receipt linked');
      fetchData();
    } catch { toast.error('Failed'); }
  };

  if (loading) return <Layout title="Credit Card Controls"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D98A4A', borderTopColor: 'transparent' }}></div></div></Layout>;

  const { statement_matches = [], employee_exceptions = [], interest_fees = [], refunds = [], personal_flags = [], missing_receipts = [], interest_fee_total = 0, refund_total = 0, personal_flag_count = 0, missing_receipt_count = 0, employee_exception_count = 0 } = data || {};

  return (
    <Layout title="Credit Card Controls">
      <div data-testid="credit-card-page" className="space-y-6">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <StatCard label="Statement Match" value={statement_matches.length} sub="accounts" icon={ArrowLeftRight} color="#D98A4A" />
          <StatCard label="Personal Flags" value={personal_flag_count} icon={User} color="#C84B31" />
          <StatCard label="Employee Exceptions" value={employee_exception_count} icon={UserX} color="#D98A4A" />
          <StatCard label="Interest & Fees" value={`$${interest_fee_total.toLocaleString()}`} icon={DollarSign} color="#C84B31" />
          <StatCard label="Missing Receipts" value={missing_receipt_count} icon={FileText} color="#9CA3AF" />
        </div>

        <Tabs defaultValue="personal">
          <TabsList className="p-1 rounded-xl" style={{ background: '#E5E3DB' }}>
            <TabsTrigger value="personal" className="rounded-lg font-body data-[state=active]:bg-white" data-testid="tab-personal">
              <User size={14} className="mr-1.5" /> Personal vs Business ({personal_flag_count})
            </TabsTrigger>
            <TabsTrigger value="employee" className="rounded-lg font-body data-[state=active]:bg-white" data-testid="tab-employee">
              <UserX size={14} className="mr-1.5" /> Employee ({employee_exception_count})
            </TabsTrigger>
            <TabsTrigger value="fees" className="rounded-lg font-body data-[state=active]:bg-white" data-testid="tab-fees">
              <DollarSign size={14} className="mr-1.5" /> Fees & Interest ({interest_fees.length})
            </TabsTrigger>
            <TabsTrigger value="refunds" className="rounded-lg font-body data-[state=active]:bg-white" data-testid="tab-refunds">
              <RotateCcw size={14} className="mr-1.5" /> Refunds ({refunds.length})
            </TabsTrigger>
            <TabsTrigger value="receipts" className="rounded-lg font-body data-[state=active]:bg-white" data-testid="tab-receipts">
              <FileText size={14} className="mr-1.5" /> Missing Receipts ({missing_receipt_count})
            </TabsTrigger>
          </TabsList>

          {/* Personal vs Business */}
          <TabsContent value="personal" className="mt-4">
            <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
              <div className="px-5 py-3 flex items-center gap-2" style={{ borderBottom: '1px solid #E5E3DB', background: 'rgba(198,156,109,0.04)' }}>
                <AlertTriangle size={14} style={{ color: '#C84B31' }} />
                <span className="text-xs font-body font-semibold" style={{ color: '#4B5563' }}>Transactions from merchants that may be personal expenses</span>
              </div>
              {personal_flags.length > 0 ? personal_flags.map((txn) => (
                <TxnRow key={txn.transaction_id} txn={txn} actions={<>
                  <button onClick={() => handleFlag(txn.transaction_id, 'business')} className="px-2.5 py-1 rounded-lg text-[10px] font-body font-semibold flex items-center gap-1" style={{ background: 'rgba(45,106,79,0.1)', color: '#2D7A5D' }} data-testid={`flag-business-${txn.transaction_id}`}><Briefcase size={10} /> Business</button>
                  <button onClick={() => handleFlag(txn.transaction_id, 'personal')} className="px-2.5 py-1 rounded-lg text-[10px] font-body font-semibold flex items-center gap-1" style={{ background: 'rgba(224,122,95,0.1)', color: '#C84B31' }} data-testid={`flag-personal-${txn.transaction_id}`}><User size={10} /> Personal</button>
                </>} />
              )) : <div className="p-8 text-center text-sm font-body" style={{ color: '#9CA3AF' }}>No personal-use flags detected</div>}
            </div>
          </TabsContent>

          {/* Employee Exceptions */}
          <TabsContent value="employee" className="mt-4">
            <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
              {employee_exceptions.length > 0 ? employee_exceptions.map((txn) => (
                <TxnRow key={txn.transaction_id} txn={txn} actions={<>
                  <button onClick={() => handleFlag(txn.transaction_id, 'reviewed')} className="px-2.5 py-1 rounded-lg text-[10px] font-body font-semibold" style={{ background: 'rgba(45,106,79,0.1)', color: '#2D7A5D' }}><CheckCircle2 size={10} className="inline mr-1" />Approve</button>
                </>} />
              )) : <div className="p-8 text-center text-sm font-body" style={{ color: '#9CA3AF' }}>No employee card exceptions</div>}
            </div>
          </TabsContent>

          {/* Interest & Fees */}
          <TabsContent value="fees" className="mt-4">
            <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
              <div className="px-5 py-3" style={{ borderBottom: '1px solid #E5E3DB', background: 'rgba(157,2,8,0.03)' }}>
                <span className="text-xs font-body font-semibold" style={{ color: '#C84B31' }}>Total: ${interest_fee_total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              {interest_fees.length > 0 ? interest_fees.map((txn) => (
                <TxnRow key={txn.transaction_id} txn={txn} actions={<>
                  <button onClick={() => handleTag(txn.transaction_id, 'interest')} className="px-2 py-1 rounded-lg text-[10px] font-body" style={{ background: 'rgba(198,156,109,0.1)', color: '#D98A4A' }}>Interest</button>
                  <button onClick={() => handleTag(txn.transaction_id, 'fee')} className="px-2 py-1 rounded-lg text-[10px] font-body" style={{ background: 'rgba(161,161,165,0.1)', color: '#4B5563' }}>Fee</button>
                </>} />
              )) : <div className="p-8 text-center text-sm font-body" style={{ color: '#9CA3AF' }}>No interest or fee charges found</div>}
            </div>
          </TabsContent>

          {/* Refunds */}
          <TabsContent value="refunds" className="mt-4">
            <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
              <div className="px-5 py-3" style={{ borderBottom: '1px solid #E5E3DB', background: 'rgba(45,106,79,0.03)' }}>
                <span className="text-xs font-body font-semibold" style={{ color: '#2D7A5D' }}>Total refunds: ${refund_total.toLocaleString('en-US', { minimumFractionDigits: 2 })}</span>
              </div>
              {refunds.length > 0 ? refunds.map((txn) => (
                <TxnRow key={txn.transaction_id} txn={txn} actions={<>
                  <button onClick={() => handleTag(txn.transaction_id, 'refund')} className="px-2.5 py-1 rounded-lg text-[10px] font-body font-semibold" style={{ background: 'rgba(45,106,79,0.1)', color: '#2D7A5D' }}><CheckCircle2 size={10} className="inline mr-1" />Tag Refund</button>
                </>} />
              )) : <div className="p-8 text-center text-sm font-body" style={{ color: '#9CA3AF' }}>No refunds detected</div>}
            </div>
          </TabsContent>

          {/* Missing Receipts */}
          <TabsContent value="receipts" className="mt-4">
            <div className="bg-white rounded-xl overflow-hidden" style={{ border: '1px solid #E5E3DB' }}>
              <div className="px-5 py-3" style={{ borderBottom: '1px solid #E5E3DB', background: 'rgba(161,161,165,0.03)' }}>
                <span className="text-xs font-body font-semibold" style={{ color: '#4B5563' }}>{missing_receipt_count} transactions over $25 without receipts</span>
              </div>
              {missing_receipts.length > 0 ? missing_receipts.map((txn) => (
                <TxnRow key={txn.transaction_id} txn={txn} actions={<>
                  <button onClick={() => handleLinkDoc(txn.transaction_id)} className="px-2.5 py-1 rounded-lg text-[10px] font-body font-semibold flex items-center gap-1" style={{ background: 'rgba(198,156,109,0.1)', color: '#D98A4A' }} data-testid={`link-receipt-${txn.transaction_id}`}><Paperclip size={10} /> Link Receipt</button>
                </>} />
              )) : <div className="p-8 text-center text-sm font-body" style={{ color: '#9CA3AF' }}>All receipts linked</div>}
            </div>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
